<div class="fullcontainer pr-3">
  <div class='sheet'>
    <div class='titleContainer'>
      <h1 class='header'>Report - Adjustments</h1>
      <div>
        <button class='btn btn-secondary' routerLink='/warehousing/reports'>All Reports</button>
      </div>
    </div>

    <p class='text-muted'>
      Stock written on or off by hand, outside a delivery or collection. An adjustment
      quantity is signed: a negative quantity took stock off.
    </p>

    <p class='text-muted'>
      Includes movements posted as adjustments <em>and</em> stock written off as a goods-out
      against DAMAGED, DISPOSAL, SHORT, OVER or STOCKTAKE — write-offs are classified by
      category, not by movement type alone.
    </p>

    <div class='row'>
      <div class='col-md-4'>
        <label>Start Date</label>
        <input type='date' class='form-control'
          [class.is-invalid]='this.datesInvalid'
          [(ngModel)]='this.searchFilters.startDate' />
      </div>
      <div class='col-md-4'>
        <label>End Date</label>
        <input type='date' class='form-control'
          [class.is-invalid]='this.datesInvalid'
          [(ngModel)]='this.searchFilters.endDate' />
        @if (this.datesInvalid) {
          <div class='invalid-feedback d-block'>The end date is before the start date.</div>
        }
      </div>
      <div class='col-md-4'>
        <label>Warehouse</label>
        <select class='form-control' (change)='this.onWarehouseChanged($event.target.value)'>
          <option value='-1'>All warehouses</option>
          @for (item of this.warehouses; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
      <div class='col-md-4'>
        <label>Product</label>
        <select class='form-control' [(ngModel)]='this.searchFilters.productId'>
          <option [value]='-1'>All products</option>
          @for (item of this.products; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
    </div>
    <br />
    <br />
    <button class='btn btn-primary' (click)='this.loadReport()'
      [disabled]='this.running || this.datesInvalid'>Run Report</button>
    <hr />
    <button class='btn btn-secondary' (click)='exportToCSV()' [disabled]='this.adjustments.length === 0'>Export to CSV</button>
  </div>

  <div class='sheet'>
    @if (this.running) {
      <p class='m-0'><i class="fas fa-spinner fa-spin"></i> Running&hellip;</p>
    }

    @if (this.hasRun && !this.running && this.adjustments.length === 0) {
      <p class='m-0 text-muted'>No adjustments matched those filters.</p>
    }

    @if (this.adjustments.length > 0) {
      <div class='titleContainer'>
        <h1 class='header'>Results</h1>
      </div>

      <h3>NET ADJUSTMENT: {{ this.getNetQty() }}</h3>
      <p class='text-muted'>
        {{ this.totals.movements }} adjustment(s).
        Written on {{ this.formatQty(this.totals.increaseQty) }},
        written off {{ this.formatQty(this.totals.decreaseQty) }}.
        A net near zero does not mean nothing happened — check both directions.
      </p>

      <!-- Value never appears without the coverage beside it. `cost` is nullable: NULL means
           never priced, 0 means deliberately priced at zero for 3PL stock the customer owns.
           So a bare money total silently omits every unit nobody has priced, and on this
           dataset that is currently all of them. Called "at cost", never "asset value" —
           much of this warehouse belongs to Blacks Haulage's customers, not to them. -->
      <div class='row'>
        <div class='col-md-6'>
          <div class='sheet mt-0'>
            <h6 class='text-muted mb-1'>Written off, at cost</h6>
            <h3 class='header mb-1'>{{ this.formatMoney(this.totals.decreaseValue) }}</h3>
            @if (this.hasUncosted) {
              <p class='text-danger mb-0'>
                <small>
                  <i class="fas fa-exclamation-triangle"></i>
                  Covers only part of what moved —
                  {{ this.totals.uncostedMovements }} of {{ this.totals.movements }} movement(s)
                  carry no unit cost ({{ this.formatQty(this.totals.uncostedUnits) }} units),
                  and {{ this.totals.productsWithoutCostBasis }} product(s) have no cost recorded.
                  Do not use this figure for a claim without pricing those first.
                </small>
              </p>
            }
            @if (!this.hasUncosted) {
              <p class='text-muted mb-0'><small>Every movement in this period carries a unit cost.</small></p>
            }
          </div>
        </div>
        <div class='col-md-6'>
          <div class='sheet mt-0'>
            <h6 class='text-muted mb-1'>Net value change, at cost</h6>
            <h3 class='header mb-1'>{{ this.formatMoney(this.totals.netValue) }}</h3>
            <p class='text-muted mb-0'>
              <small>Written on {{ this.formatMoney(this.totals.increaseValue) }} ·
              written off {{ this.formatMoney(this.totals.decreaseValue) }}.</small>
            </p>
          </div>
        </div>
      </div>

      <!-- Net weight never appears without the coverage count beside it, same reasoning as the
           value box above: `SUM()` skips what it cannot add, so a net weight on its own is a real
           number that says nothing about how much of the set it covers. The count is drawn as a
           literal number in every case, including zero — a figure that only appears when there
           is a problem cannot be told from one somebody forgot to draw. Drawn from the backend
           total, over the whole filtered set, never recomputed here: recomputing it over the rows
           on screen would silently narrow it to the row cap, and re-stating the transfer-in
           exclusion in a second place would be a second answer to what the goods weighed. -->
      <div class='row'>
        <div class='col-md-6'>
          <div class='sheet mt-0'>
            <h6 class='text-muted mb-1'>Net weight moved</h6>
            <h3 class='header mb-1'>{{ this.formatWeight(this.totals.netWeight) }}</h3>
            <p [class.text-danger]='this.hasUnweighed' [class.text-muted]='!this.hasUnweighed' class='mb-0'>
              <small>
                @if (this.hasUnweighed) {
                  <i class="fas fa-exclamation-triangle"></i>
                }
                Weighed at both ends: {{ this.totals.weighedMovements }} of {{ this.totals.movements }}
                movement(s). Not weighed, or weighed once: {{ this.totals.unweighedMovements }}
                movement(s) — these are not in the total. Weights are recorded for reporting and
                checked against nothing: an end weight below the start is the expected reading on
                stock going out. A transfer is one weighing recorded on two rows, so only its
                outward row counts toward this total.
              </small>
            </p>
          </div>
        </div>
      </div>

      <h4 class='header'>By Reference</h4>
      <p class='text-muted'>
        Where the write-offs were booked. This is the breakdown an insurer or a customer will
        ask for.
      </p>
      <table class='table'>
        <thead>
          <th>Reference</th>
          <th>Code</th>
          <th class='text-right'>Movements</th>
          <th class='text-right'>Net Qty</th>
          <th class='text-right'>Net Value</th>
          <th class='text-right'>Uncosted</th>
        </thead>
        <tbody>
          @for (row of this.byReference; track row.referenceId) {
            <tr>
              <td>{{ row.referenceName }}</td>
              <td>{{ row.referenceCode }}</td>
              <td class='text-right'>{{ row.movements }}</td>
              <td class='text-right' [class.text-danger]='this.isNegative(row.netQty)'>
                {{ this.formatQty(row.netQty) }}
              </td>
              <td class='text-right'>{{ this.formatMoney(row.netValue) }}</td>
              <!-- Movements with no unit cost behind them, so the value above understates. -->
              <td class='text-right' [class.text-danger]='row.uncostedMovements > 0'>
                {{ row.uncostedMovements }}
              </td>
            </tr>
          }
        </tbody>
      </table>

      <hr />

      <h4 class='header'>Every Adjustment</h4>
      <table class='table'>
        <thead>
          <th>Id</th>
          <th>Date</th>
          <th>Product</th>
          <th>SKU</th>
          <th>Type</th>
          <th>Warehouse</th>
          <th>Location</th>
          <th class='text-right'>Signed Qty</th>
          <th class='text-right'>Start Weight</th>
          <th class='text-right'>End Weight</th>
          <th class='text-right'>Net Weight</th>
          <th>Reference</th>
          <th>Reference Code</th>
          <th>Created By</th>
        </thead>
        <tbody>
          @for (adjustment of this.adjustments; track adjustment.id) {
            <tr>
              <td>{{ adjustment.id }}</td>
              <td>{{ this.formatDateTime(adjustment.createdAt) }}</td>
              <td>{{ adjustment.productName }}</td>
              <td>{{ adjustment.sku }}</td>
              <td>{{ adjustment.movementType }}</td>
              <td>{{ adjustment.warehouseName }}</td>
              <td>{{ adjustment.locationLabel }}</td>
              <!-- signedQty is what the movement did to the level. Negative is normal for an
                   adjustment, so it is coloured rather than flagged as a defect. -->
              <td class='text-right' [class.text-danger]='this.isNegative(adjustment.signedQty)'>
                {{ this.formatQty(adjustment.signedQty) }}
              </td>
              <!-- Blank on a movement that was not weighed, and blank on the net where only one
                   reading was taken. Not coloured when the net is negative, unlike the qty
                   beside it: a falling weight is the expected reading on stock going out, so
                   colouring it would flag the ordinary case as a problem. -->
              <td class='text-right'>{{ this.formatWeight(adjustment.startWeight) }}</td>
              <td class='text-right'>{{ this.formatWeight(adjustment.endWeight) }}</td>
              <td class='text-right'>{{ this.formatWeight(adjustment.netWeight) }}</td>
              <td>{{ adjustment.referenceName }}</td>
              <td>{{ adjustment.referenceCode }}</td>
              <td>{{ adjustment.createdBy }}</td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>
</div>
